import VeeValidate from 'vee-validate'
import Vue from 'vue'
import PhoneNumber from 'awesome-phonenumber'
import { availableAreaCodes } from './areacodes'
import { store } from '@/store'
import { UserRole } from '@/types/User.interface'
import Group from '@/types/Group.interface'

VeeValidate.Validator.extend('accept_terms', {
    getMessage: _ => 'Please accept the Terms & Conditions before continuing',
    validate: (value: boolean): boolean => {
        return value !== false
    }
})

VeeValidate.Validator.extend('alpha_num_spaces', {
    getMessage: (field: string) => `${field} must contain letters, numbers, and spaces only`,
    validate: (value: string): boolean => {
        return /^[\w\-\s]+$/.test(value)
    }
})

VeeValidate.Validator.extend('areacode', {
    getMessage: _ => 'This area code is not currently supported.',
    validate: (value: string): boolean => {
        //  Grab our available area codes
        const available = availableAreaCodes.codes
        //  Check if the value matches any of these codes
        const matches = available.includes(parseInt(value))
        //  If it does, passes
        return matches
    }
})

VeeValidate.Validator.extend('color', {
    getMessage: _ => `Must be a valid color`,
    validate: (value: string): boolean => {
        // Create a style element and store it in a variable
        const s = new Option().style

        // Set the style color to the value given
        s.color = value

        // Passes if CSS sees that it is a proper color
        return s.color !== ''
    }
})

VeeValidate.Validator.extend('group_not_empty', {
    getMessage: _ => 'This group has no contacts.',
    validate: (group: Group): boolean => {
        //  If the persons_count does NOT exist, validation fails
        if (!group.persons_count) return false

        //  IF the persons_count is zero, validation fails
        if (group.persons_count === 0) return false

        //  Else the group has atleast one contact, validation passes
        return true
    }
})

VeeValidate.Validator.extend('decimal', {
    getMessage: (field: string) => `${field} must contain numbers only`,
    validate: (value: any): boolean => {
        return !isNaN(parseFloat(value)) && isFinite(value);
    }
})

VeeValidate.Validator.extend('invoiceCampaignDays', {
    getMessage: _ => `Please enter a valid value`,
    validate: (value: any): boolean => (value == parseInt(value) && value > 0 && value < 91) || value == 'On due date'
})

VeeValidate.Validator.extend('message_length', {
    getMessage: _ => 'Message too long',
    validate: (value: string, params: number[]): boolean => {
        //  Get the total character limit
        const limit = params[0]

        //  Check if the text length is under the limit
        const lengthAllowed = value.length <= limit

        //  return the result
        return lengthAllowed
    }
})

VeeValidate.Validator.extend('phone', {
    getMessage: (field: string) => `This is not a valid phone number`,
    validate (value: string): boolean {
        if (!value) return true
        //  Remove non-numeric
        value = value.replace(/\D/g,'');

        const phone = new PhoneNumber(value, 'US');

        //  Check if the phone is valid
        const isValid = phone.isValid()
        
        //  Since our awesome-number package is out of date, newer area codes will fail validation even though they are a legitmate number
        //  so if the number is "invalid" we do an additional check. Since each character entry triggers this function we need the msisdn to be beyond 5 characters 
        //  before we check the area code ("+17751") is when we can check area code
        if (!isValid && value.length >= 5) {
            //  We get the phone number entered in +17755550234 format, we cut off the +1 and then grab just the area code (775)
            const areacode = phone.getNumber('e164').slice(2).slice(0,3)
            
            //  We then grab our list of permitted area codes and see if the area code is in this list, if it is then this number is valid
            const areacodeIsValid = availableAreaCodes.codes.includes(parseInt(areacode))

            return areacodeIsValid
        }

        return isValid
    }
})

VeeValidate.Validator.extend('userPhoneUnique', {
    getMessage: field => `An account already exists with this phone number`,
    async validate (value: string): Promise<boolean> {
        if (!value) return true

        // Check if a user already exists with the phone number
        const resp = await store.dispatch('mobilephoneExists', value)

        const exists = resp.exists

        // Valid if the user does not exist
        return !exists
    }
})

VeeValidate.Validator.extend('userEmailUnique', {
    getMessage: _ => `An account already exists with this email`,
    async validate (value: string): Promise<boolean> {
        if (!value) return true

        // Check if a user already exists with the phone number
        const resp = await store.dispatch('emailExists', value)

        const exists = resp.exists

        // Valid if the user does not exist
        return !exists
    }
})

VeeValidate.Validator.extend('is_not_keyword', {
    getMessage: _ => `The 'unsubscribe' keyword must be different from the 'subscribe' keyword.`,
    validate (value: string, params: string[]): boolean {
        return value !== params[0];
    }
})

VeeValidate.Validator.extend('support_check', {
    getMessage: _ => 'Support role can only be selected to invite MessageDesk support members.',
    validate: (value: UserRole, params: string[]): boolean => {
        //  Grab our email from the validation params
        const email = params[0]
        //  Check if the value is support
        const support = value.name === 'Support' || value.permission === 4

        //  Check if email contains messagedesk or snapdesk
        const valid = email.includes('@snapdesk.app') || email.includes('@messagedesk.com')

        //  If it isn't the support role, just return true
        if (!support) return true
        //  If it is the support role, but the email is valid, return true
        else if (support && valid) return true
        //  If support is true, but valid is false, return false
        else if (support && !valid) return false
        //  If we somehow get here, return false
        return false
    }
})

VeeValidate.Validator.extend('verify_password', {
    getMessage: _ => 'Passwords must be at least 8 characters and contain 1 uppercase letter, 1 lowercase letter, 1 number, and one special character.',
    validate(value: string): boolean {
        // var synopsysRegex = new RegExp('^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*])(?=.{10,})(?!.*([A-Za-z0-9])\\1{1})')
        var strongRegex = new RegExp('^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#\$%\^&\*\"\'\(\)\+\-\,\.\/\:\<\=\>\?\\~\`\_])(?=.{8,})')
        return strongRegex.test(value)
    }
})

VeeValidate.Validator.extend('verify_url', {
    getMessage: _ => 'Web address must use https:// format in URL.',
    validate(value: string): boolean {
        var urlRegex = new RegExp(/^(https?:\/\/)[a-z0-9]+([\-\.]{1}[a-z0-9]+)*\.[a-z]{2,5}(:[0-9]{1,5})?(\/.*)?$/)
        return urlRegex.test(value) || value === null || value===''
    }
})

Vue.use(VeeValidate)

export default VeeValidate
